<template>
  <div class="wg-card" id="wg-details">
    <div class="wg-sec-title">Project Details</div>
    <div class="wg-sec-sub">Basic information about this project.</div>

    <div class="wg-form">
      <div class="wg-fld">
        <label>Site URL</label>
        <input class="wg-input" v-model="form.siteUrl" type="text" />
      </div>
      <div class="wg-fld">
        <label>Project name</label>
        <input class="wg-input" v-model="form.projectName" type="text" />
      </div>
      <div class="wg-fld">
        <label>Target country</label>
        <div class="wg-select-wrap">
          <select class="wg-select" v-model="form.locationCode" :disabled="!campaign">
            <option v-for="loc in locations" :key="loc.location_code" :value="loc.location_code">
              {{ loc.location_name }}
            </option>
          </select>
          <i class="material-icons chev">expand_more</i>
        </div>
        <div v-if="!campaign" class="wg-fld-hint">
          Target country becomes editable once a domain-level campaign exists for this project.
        </div>
      </div>
      <div class="wg-fld">
        <label>Target language</label>
        <div class="wg-select-wrap">
          <select class="wg-select" v-model="form.languageCode" :disabled="!campaign">
            <option v-for="lang in languages" :key="lang.language_code" :value="lang.language_code">
              {{ lang.language_name }}
            </option>
          </select>
          <i class="material-icons chev">expand_more</i>
        </div>
      </div>
    </div>

    <div v-if="urlChanged" class="wg-notice">
      <span class="ico"><i class="material-icons-outlined">warning</i></span>
      <div class="body">
        <div class="nt">You're changing the site URL</div>
        <div class="nb">
          If Search Console is connected, its property may no longer match this domain and tracking
          could stop until you reconnect.
        </div>
      </div>
    </div>

    <div class="wg-card-divider"></div>

    <button class="wg-pill outline" :disabled="saving || !canSave" @click="save">
      <span v-if="saving" class="wg-spin dark"></span>
      {{ saving ? 'Saving…' : 'Save project details' }}
    </button>
  </div>
</template>

<script>
import dataForSeoLocations from '@/consts/dataforseo_locations.json'
import languages from '@/consts/dataforseo_languages.json'

export default {
  name: 'ProjectDetailsCard',
  inject: ['wgToast'],
  props: {
    site: {
      type: Object,
      default: () => ({}),
    },
    campaign: {
      type: Object,
      default: null,
    },
  },
  data () {
    return {
      form: {
        siteUrl: '',
        projectName: '',
        locationCode: '',
        languageCode: '',
      },
      saving: false,
      locations: dataForSeoLocations.locations,
      languages: languages,
    }
  },
  computed: {
    canSave () {
      return Boolean((this.form.projectName || '').trim() && (this.form.siteUrl || '').trim())
    },
    urlChanged () {
      const current = (this.site && this.site.baseURL) || ''
      return Boolean(current && this.form.siteUrl && this.form.siteUrl.trim() !== current)
    },
  },
  watch: {
    site: {
      handler () {
        this.seedFromSite()
      },
      immediate: true,
    },
    campaign: {
      handler () {
        this.seedFromCampaign()
      },
      immediate: true,
    },
  },
  methods: {
    seedFromSite () {
      if (!this.site) return
      this.form.siteUrl = this.site.baseURL || this.site.base_url || ''
      this.form.projectName = this.site.name || ''
    },
    seedFromCampaign () {
      if (!this.campaign) return
      const code = parseInt(this.campaign.locationCode, 10)
      this.form.locationCode = Number.isFinite(code) ? code : this.campaign.locationCode
      this.form.languageCode = this.campaign.languageCode || ''
    },
    save () {
      if (!this.canSave || this.saving) return
      this.saving = true

      const siteId = this.site.id
      const body = {}
      if (this.form.projectName.trim() !== this.site.name) {
        body.name = this.form.projectName.trim()
      }
      if (this.urlChanged) {
        body.url = this.form.siteUrl.trim()
      }

      const calls = []
      if (Object.keys(body).length > 0) {
        calls.push(this.$store.dispatch('updateSite', { siteId, body }))
      }
      if (this.campaign && this.campaignChanged()) {
        calls.push(this.$store.dispatch('updateCampaign', {
          campaignId: this.campaign.id,
          body: {
            locationCode: this.form.locationCode,
            languageCode: this.form.languageCode,
          },
        }))
      }

      Promise.all(calls)
        .then((responses) => {
          const failed = responses.find((r) => !(r.data && r.data.status === 'SUCCESS'))
          if (failed) {
            const msg = (failed.data && failed.data.msg) || 'Could not save project details'
            this.wgToast(msg, null, 'error')
            return
          }
          this.wgToast('Project details saved')
          this.refreshSite()
          this.$emit('refresh-campaigns')
        })
        .catch(() => {
          this.wgToast('Could not save project details', null, 'error')
        })
        .finally(() => {
          this.saving = false
        })
    },
    campaignChanged () {
      if (!this.campaign) return false
      const currentCode = parseInt(this.campaign.locationCode, 10)
      const formCode = parseInt(this.form.locationCode, 10)
      return currentCode !== formCode || (this.campaign.languageCode || '') !== this.form.languageCode
    },
    refreshSite () {
      const siteId = this.site.id
      if (!siteId) return
      this.$store.dispatch('loadSite', siteId).then((res) => {
        if (res.data && res.data.status === 'SUCCESS' && res.data.site) {
          this.$store.commit('setSite', res.data.site)
        }
      })
    },
  },
}
</script>

<style lang="scss" scoped>
@import './settings-card.scss';

.wg-fld-hint {
  font-size: 0.8rem;
  color: $pop-ink-faint;
  font-style: italic;
}
</style>
